import React from 'react';
import { SectionPanel } from 'ui';

import { ComplexValue, LoadOptions } from 'app/types-shared';
import { MetroLineSection } from 'app/components/metro-line/metro-line-section';
import { WithMetroLineWrappedComponentProps } from 'app/components/metro-line/types';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';

import { SwitchDisplayDisabled } from 'app/area/shared/saved-reports/components/switch-display-disabled';
import { SavedReports } from 'app/area/shared/saved-reports/form/saved-reports';
import { SavedReportCategory, notLimitedOpenDashboard } from 'app/area/shared/saved-reports/constants';
import { SavedReportsValidation, SavedReportValidation } from 'app/area/shared/saved-reports/types-store';
import { AddSavedReportsHandler, OnUpdateSavedReport, UpdateSavedReportHandler } from 'app/area/shared/saved-reports/types';

import { SavedReportForm } from '../../common/components/saved-report-form';
import { createDefaultSavedReport } from '../../common/create-default-saved-report';
import { endpoints } from '../../common/endpoints';
import { validateSavedReport } from '../../common/validate-saved-report';
import { SavedReportDashboard } from '../../common/types-store';
import { DashboardAdminTranslation } from '../../constants';
import { DashboardSectionType } from '../metroline/types';

interface DashboardSavedReportsProps extends WithMetroLineWrappedComponentProps<DashboardSectionType> {
  savedReports: SavedReportDashboard[];
  currentSavedReportId: number;
  currentSavedReport: SavedReportDashboard;
  currentSavedReportOnlyIdMode: boolean;
  accessType: number;
  currentValidation: SavedReportValidation<SavedReportDashboard>;
  displayDisabled: boolean;

  addSavedReports: AddSavedReportsHandler<SavedReportDashboard>;
  updateSavedReport: UpdateSavedReportHandler<SavedReportDashboard>;
  deleteSavedReport: (savedReportId: number) => void;
  deleteAllSavedReports: () => void;

  fetchingAddSavedReports: boolean;

  selectCurrent: (savedReport: SavedReportDashboard, onlyIdMode: boolean) => void;
  updateCurrent: (savedReport: Partial<SavedReportDashboard>) => void;
  updateValidation: (validation: SavedReportValidation<SavedReportDashboard>) => void;

  loadEvaluation: (accessType: number, favouriteId: number, onUpdateSavedReport: OnUpdateSavedReport<SavedReportDashboard>) => void;
  displayError: (errorMessage: string) => void;
  switchDisplayDisabled: (displayDisabled: boolean) => void;

  options: {
    [key: string]: {
      fetching: boolean;
      codes?: ComplexValue<number | string>[];
    };
  };
  loadOptions: (id: string,
    componentEndpoint: string,
    loadOptionsParams: LoadOptions,
    optionsParams?: any) => void;

  locale: string;
  savedReportsValidation: SavedReportsValidation<SavedReportDashboard>;
  translation: DashboardAdminTranslation;

  section: string;
}

export const DashboardSavedReports: React.FC<DashboardSavedReportsProps> = ({
  savedReports, currentSavedReportId, currentSavedReport, currentSavedReportOnlyIdMode, currentValidation,
  addSavedReports, updateSavedReport, deleteSavedReport, deleteAllSavedReports, fetchingAddSavedReports,
  accessType, displayDisabled, switchDisplayDisabled,
  selectCurrent, updateCurrent, updateValidation,
  loadEvaluation, displayError,
  options, loadOptions,
  locale, savedReportsValidation, translation,
  section, getRef, shouldRenderSection
}) => {

  const renderSavedReportInfo = (savedReport: SavedReportDashboard) => <>
    <div>{translation.minWidth}: {savedReport?.minWidth}</div>
    {isDefinedIncludingZero(savedReport?.height) && <div>{translation.height}: {savedReport?.height}</div>}
  </>;

  const renderSavedReportForm = (savedReport: SavedReportDashboard) =>
    <SavedReportForm
      savedReport={savedReport}
      updateCurrent={updateCurrent}
      endpoints={endpoints}
      options={options}
      loadOptions={loadOptions}
      locale={locale}
      validation={currentValidation}
      translation={translation} />;

  const savedReportCategories =
    accessType === notLimitedOpenDashboard
      ? [
        SavedReportCategory.riskReport,
        SavedReportCategory.statisticalReport
      ]
      : [
        SavedReportCategory.caseReport,
        SavedReportCategory.statisticalReport,
        SavedReportCategory.riskReport,
        SavedReportCategory.numeratorReport
      ];

  return <MetroLineSection<DashboardSectionType>
    section={section}
    sectionType={DashboardSectionType.SavedReports}
    getRef={getRef}
    shouldRenderSection={shouldRenderSection}>
    <div ref={getRef(DashboardSectionType.SavedReports)}>
      <SectionPanel
        className="admin-paper-top-panel"
        componentId={DashboardSectionType.SavedReports}
        validation={undefined}
        info={{ text: translation.saved_reports }}
        label={{ visible: true, text: translation.saved_reports }} />
      <SavedReports
        savedReports={savedReports}
        currentSavedReportId={currentSavedReportId}
        currentSavedReport={currentSavedReport}
        currentSavedReportOnlyIdMode={currentSavedReportOnlyIdMode}
        currentValidation={currentValidation}
        displayDisabled={displayDisabled}
        switchDisplayDisabled={switchDisplayDisabled}
        renderSavedReportInfo={renderSavedReportInfo}
        renderSavedReportForm={renderSavedReportForm}
        createDefaultSavedReport={createDefaultSavedReport}
        addSavedReports={addSavedReports}
        updateSavedReport={updateSavedReport}
        deleteSavedReport={deleteSavedReport}
        deleteAllSavedReports={deleteAllSavedReports}
        fetchingAddSavedReports={fetchingAddSavedReports}
        validateSavedReport={validateSavedReport}
        selectCurrent={selectCurrent}
        updateValidation={updateValidation}
        loadEvaluation={loadEvaluation}
        displayError={displayError}
        endpoints={endpoints}
        accessType={accessType}
        savedReportCategories={savedReportCategories}
        options={options}
        loadOptions={loadOptions}
        savedReportsValidation={savedReportsValidation}
        translation={translation}
        locale={locale}
        header={<SwitchDisplayDisabled
          label={translation.show_disabled_elements}
          active={displayDisabled}
          onChange={switchDisplayDisabled} />}
        tileConfig={{
          add: true,
          edit: true,
          move: true,
          delete: true
        }}
      />
    </div>
  </MetroLineSection>;
};
